import React, { useState } from 'react';
import { useSupervisorAuth } from '../../context/SupervisorAuthContext';
import SupervisorLayout from '../../components/SupervisorLayout';
import LotteryCodeField from '../../components/LotteryCodeField';
import SupervisorGroupCodeField from '../../components/SupervisorGroupCodeField';
import SupervisorBankCodeField from '../../components/SupervisorBankCodeField';
import DateField from '../../components/DateField';
import { fetchSupervisorCumulDetaille } from '../../api/supervisor';
import { todayISO } from '../../utils/date';
import { downloadXLSX } from '../../utils/xlsx';
import './SupervisorCumulDetaillePage.css';

const perteGainOptions = [
  { value: 'All', label: 'Tout' },
  { value: 'Loss', label: 'Perte' },
  { value: 'Win', label: 'Gain' },
];


const money = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SupervisorCumulDetaillePage = () => {
  const { supervisor } = useSupervisorAuth();
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [lotteryFrom, setLotteryFrom] = useState('');
  const [lotteryFromName, setLotteryFromName] = useState('');
  const [lotteryUp, setLotteryUp] = useState('');
  const [lotteryUpName, setLotteryUpName] = useState('');
  const [perteGain, setPerteGain] = useState('All');
  // Filtre Groupe / Banque optionnel, mutuellement exclusif (vide = toutes ses banks).
  const [groupId, setGroupId] = useState('');
  const [groupName, setGroupName] = useState('');
  const [bankId, setBankId] = useState('');
  const [bankName, setBankName] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await fetchSupervisorCumulDetaille({
        dateFrom, dateTo, lotteryFrom, lotteryUp, perteGain, group: groupId, bank: bankId,
      });
      setData(result);
    } catch (err) {
      setError('Recherche impossible.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExcel = () => {
    const f2 = (n) => Number(n || 0);
    const header = [
      'Banque (ID POS)', 'Entreprise / Username', 'Vendu', 'Gain',
      'Commission', 'Profit/Perte',
    ];
    const dataRows = data.rows.map((r) => [
      `${r.bank_seq} - ${r.bank_name}`,
      r.bank_username || r.device_serial ? `${r.company_name} (${r.bank_username || r.device_serial})` : r.company_name,
      f2(r.vendu),
      f2(r.gagne),
      f2(r.commission),
      f2(r.total),
    ]);
    const totalRow = [
      'Total', '',
      f2(data.grand_total.vendu),
      f2(data.grand_total.gagne),
      f2(data.grand_total.commission),
      f2(data.grand_total.total),
    ];
    const sheet = [
      [supervisor?.company_name || data.rows?.[0]?.company_name || ''],
      ['Cumul détaillé'],
      [`Supervisor: ${supervisor?.username || supervisor?.name || '—'}`],
      [`Période: ${dateFrom} — ${dateTo}`],
      [`Date du rapport: ${todayISO()}`],
      [],
      header,
      ...dataRows,
      totalRow,
    ];
    downloadXLSX(`cumul-detaille_${dateFrom}_${dateTo}.xlsx`, sheet, {
      sheetName: 'Cumul detaille',
      headerRow: 6,
      boldRows: [0, sheet.length - 1],
    });
  };

  return (
    <SupervisorLayout>
      <h2 className="svcd-title">Cumul détaillé{supervisor?.company_name ? ` — ${supervisor.company_name}` : ""}</h2>

      <div className="svcd-card">
        <form onSubmit={handleSearch} className="svcd-form">
          <div className="svcd-row">
            <label className="svcd-label">De</label>
            <DateField className="svcd-input" value={dateFrom} onChange={setDateFrom} />
            <label className="svcd-label svcd-label-to">À</label>
            <DateField className="svcd-input" value={dateTo} onChange={setDateTo} />
          </div>

          <div className="svcd-row">
            <label className="svcd-label">Loterie De</label>
            <LotteryCodeField
              value={lotteryFrom}
              name={lotteryFromName}
              onChange={(code) => { setLotteryFrom(code); setLotteryFromName(''); }}
              onResolve={(code, nm) => { setLotteryFrom(code); setLotteryFromName(nm); }}
              inputClass="svcd-input svcd-input-sm"
              iconClass="svcd-icon-btn"
              nameClass="svcd-scope-name"
            />

            <label className="svcd-label svcd-label-to">Loterie À</label>
            <LotteryCodeField
              value={lotteryUp}
              name={lotteryUpName}
              onChange={(code) => { setLotteryUp(code); setLotteryUpName(''); }}
              onResolve={(code, nm) => { setLotteryUp(code); setLotteryUpName(nm); }}
              inputClass="svcd-input svcd-input-sm"
              iconClass="svcd-icon-btn"
              nameClass="svcd-scope-name"
            />
          </div>

          <div className="svcd-row">
            <label className="svcd-label">Groupe</label>
            <SupervisorGroupCodeField
              value={groupId}
              name={groupName}
              onChange={(id) => { setGroupId(id); setGroupName(''); }}
              onResolve={(id, nm) => {
                setGroupId(id); setGroupName(nm);
                if (id) { setBankId(''); setBankName(''); }
              }}
              inputClass="svcd-input svcd-input-sm"
              iconClass="svcd-icon-btn"
              nameClass="svcd-scope-name"
            />

            <label className="svcd-label svcd-label-to">Banque</label>
            <SupervisorBankCodeField
              value={bankId}
              name={bankName}
              onChange={(id) => { setBankId(id); setBankName(''); }}
              onResolve={(id, nm) => {
                setBankId(id); setBankName(nm);
                if (id) { setGroupId(''); setGroupName(''); }
              }}
              inputClass="svcd-input svcd-input-sm"
              iconClass="svcd-icon-btn"
              nameClass="svcd-scope-name"
            />
          </div>

          <div className="svcd-row">
            <label className="svcd-label">Profit/Perte</label>
            <select className="svcd-select" value={perteGain} onChange={(e) => setPerteGain(e.target.value)}>
              {perteGainOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {error && <p className="svcd-error">{error}</p>}
          <button type="submit" className="svcd-search-btn" disabled={loading}>
            {loading ? 'Recherche...' : 'Chercher'}
          </button>
        </form>
      </div>

      {data && (
        <div className="svcd-card svcd-results">
          <div className="svcd-report-header">
            <p className="svcd-report-company">
              {supervisor?.company_name || data.rows?.[0]?.company_name || 'Entreprise'}
            </p>
            <h3 className="svcd-report-heading">Cumul détaillé</h3>
            <div className="svcd-report-meta">
              <span><strong>Supervisor :</strong> {supervisor?.username || supervisor?.name || '—'}</span>
              <span><strong>Période :</strong> {dateFrom} — {dateTo}</span>
              <span><strong>Date du rapport :</strong> {todayISO()}</span>
            </div>
          </div>
          {data.rows.length === 0 ? (
            <p className="svcd-empty">Aucune vente sur cette période.</p>
          ) : (
            <>
            <button type="button" className="svcd-excel-btn" onClick={handleExcel}>⬇ Excel</button>
            <table className="svcd-table">
              <thead>
                <tr>
                  <th>Banque (ID POS)</th>
                  <th>Entreprise / Username</th>
                  <th>Vendu</th>
                  <th>Gain</th>
                  <th>Commission</th>
                  <th>Profit/Perte</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.bank_id}>
                    <td>{row.bank_seq} - {row.bank_name}</td>
                    <td>{row.bank_username || row.device_serial ? `${row.company_name} (${row.bank_username || row.device_serial})` : row.company_name}</td>
                    <td className="svcd-money">{money(row.vendu)}</td>
                    <td className="svcd-money">{money(row.gagne)}</td>
                    <td className="svcd-money">{money(row.commission)}</td>
                    <td className="svcd-money">{money(row.total)}</td>
                  </tr>
                ))}
                                <tr className="svcd-total-row">
                    <td colSpan={2}>TOTAL GÉNÉRAL</td>
                    <td className="svcd-money">{money(data.grand_total.vendu)}</td>
                    <td className="svcd-money">{money(data.grand_total.gagne)}</td>
                    <td className="svcd-money">{money(data.grand_total.commission)}</td>
                    <td className="svcd-money">{money(data.grand_total.total)}</td>
                  </tr>
</tbody>
            </table>
            <div className="svcd-total-banks" role="status">
              Nombre de POS / Total banques : <strong>{data.rows.length}</strong>
            </div>
            </>
          )}
        </div>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorCumulDetaillePage;
